import { normalizeSlaRule } from './helpers';
import { fetchProvincesForCountry, fetchCitiesForProvince } from './slaApiHelpers';

/**
 * Stable string for a rule's geo fields (used for effect deps / prefetch keys).
 * Keeps city label resolution from re-running when parent passes a new object ref for the same data.
 */
export const slaRuleGeoResolutionKey = (rule) => {
  if (!rule) return null;
  const n = normalizeSlaRule(rule);
  return `${n._id ?? ''}|${n.last_mile_warehouse ?? ''}|${JSON.stringify(n.destination_countries)}|${JSON.stringify(n.destination_cities)}`;
};

/** Key for the full rules list (table city resolution). */
export const slaRulesCityResolutionKey = (rules) =>
  (rules || []).map((raw) => slaRuleGeoResolutionKey(raw)).join('||');

/** Maps category tree nodes to "Parent > Child" path labels keyed by entity_id. */
export const buildCategoryLabelMap = (nodes, parentPath = '', acc = {}) => {
  (nodes || []).forEach((node) => {
    if (!node) return;
    const id = node.entity_id != null ? node.entity_id : node.id;
    if (id == null) return;
    const name = (node.name || '').trim() || String(id);
    const path = parentPath ? `${parentPath} > ${name}` : name;
    acc[String(id)] = path;
    if (Array.isArray(node.children) && node.children.length > 0) {
      buildCategoryLabelMap(node.children, path, acc);
    }
  });
  return acc;
};

/** From [{ id, label }] (soldBy / brandName options) → Record<id, label>. */
export const optionsToLabelMap = (options) => {
  const m = {};
  (options || []).forEach((o) => {
    if (o && o.id != null && o.label) m[String(o.id)] = o.label;
  });
  return m;
};

const provinceListCache = new Map();
const cityListCache = new Map();

const getProvincesCached = async (country, warehouse) => {
  const k = `${country}::${warehouse || ''}`;
  if (!provinceListCache.has(k)) {
    provinceListCache.set(
      k,
      await fetchProvincesForCountry({ country, warehouse })
    );
  }
  return provinceListCache.get(k);
};

const getCitiesCached = async (country, provinceId, warehouse) => {
  const k = `${country}::${String(provinceId)}::${warehouse || ''}`;
  if (!cityListCache.has(k)) {
    cityListCache.set(
      k,
      await fetchCitiesForProvince({ country, provinceId, warehouse })
    );
  }
  return cityListCache.get(k);
};

/**
 * Resolves destination city ids to display strings using the same address APIs as
 * LocationCascadeSelect. Falls back to the raw id when not found.
 *
 * @param {object[]} rules - raw SLA rules from the API
 * @param {{ getCountryName: (code: string) => string }} ctx
 */
export const resolveCityLabelsForRules = async (rules, { getCountryName }) => {
  const out = {};
  const list = Array.isArray(rules) ? rules : [];

  for (const raw of list) {
    const rule = normalizeSlaRule(raw);
    const wh = rule.last_mile_warehouse || '';
    const countryCodes = rule.destination_countries || [];
    const cityIds = (rule.destination_cities || []).map(String);

    for (const cityId of cityIds) {
      if (out[cityId] != null) continue;
      if (countryCodes.length === 0) {
        out[cityId] = cityId;
        continue;
      }
      let found = false;
      outer: for (const country of countryCodes) {
        const provinces = await getProvincesCached(country, wh);
        for (const p of provinces) {
          const cities = await getCitiesCached(country, p.id, wh);
          for (const c of cities) {
            if (String(c.id) === cityId) {
              const cityPart = c.name_en || c.name || cityId;
              out[cityId] = `${cityPart} (${getCountryName(country)})`;
              found = true;
              break outer;
            }
          }
        }
      }
      if (!found) out[cityId] = cityId;
    }
  }
  return out;
};

export const resolveCityLabelsForSingleRule = (rule, ctx) =>
  resolveCityLabelsForRules([rule], ctx);

export const mapIdsToLabels = (ids, labelById) =>
  (ids || []).map((id) => {
    const k = String(id);
    return labelById[k] != null && labelById[k] !== '' ? labelById[k] : k;
  });
